import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { apiDadata } from '@/fo/api/dadata';
import IconPlus from '@/fo/icons/base/icons/plus-circle.svg';
import IconSearch from '@/fo/icons/base/icons/search.svg';
import { UiKitAutocompleteBase } from '@/fo/ui/kit/autocomplete/base';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitInputBase } from '@/fo/ui/kit/input/base';
import {
  UiKitInputPhoto,
  SUPPORTED_IMAGE_FORMATS,
  PhotoFile
} from '@/fo/ui/kit/input/photo';
import { UiKitSelectMultiple } from '@/fo/ui/kit/select/multiple';
import { UiKitTextareaBase } from '@/fo/ui/kit/textarea/base';
import { useForm } from '@/fo/ui/mod/form/base';
import { getMediaBaseUrl } from '@/fo/util/image-loader';

import { onUploadExpertPhoto } from '../../api/onUploadExpertPhoto';
import { formAction } from '../../lib/formAction';
import { Item } from '../../types';

import { styles } from './AddSpecialistForm.styles';

export interface AddSpecialistFormProps {
  onReset?: () => void;
  occupations: Item[];
  qualifications: Item[];
}

export function AddSpecialistForm({
  occupations,
  qualifications,
  onReset
}: AddSpecialistFormProps) {
  const slots = styles();
  const t = useTranslations();
  const { state, action, pending, isDirty, getFieldProps } =
    useForm(formAction);

  const [photo, setPhoto] = useState<PhotoFile | null>(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [occupationList, setOccupationList] = useState<string[]>([]);
  const [qualificationList, setQualificationList] = useState<string[]>([]);
  const [cityInput, setCityInput] = useState('');
  const [cityOptions, setCityOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [clinicsCount, setClinicsCount] = useState(1);
  const [addressInputs, setAddressInputs] = useState(['']);
  const [addressOptions, setAddressOptions] = useState<
    { value: string; label: string }[][]
  >([[]]);
  const [longitudes, setLongitudes] = useState(['']);
  const [latitudes, setLatitudes] = useState(['']);

  const debouncedFetchCities = useDebouncedCallback((value: string) => {
    if (!value) {
      setCityOptions([]);
      return;
    }
    apiDadata('addressSuggestion', {
      query: value,
      from_bound: { value: 'city' },
      to_bound: { value: 'city' }
    }).then((res) => {
      if (
        res.type === 'addressSuggestion' &&
        'data' in res &&
        Array.isArray(res.data.suggestions)
      ) {
        setCityOptions(
          res.data.suggestions
            .filter((s: { data: { city: string | null } }) => s.data.city)
            .map((s: { data: { city: string | null } }) => ({
              value: s.data.city || '',
              label: s.data.city || ''
            }))
        );
      }
    });
  }, 500);

  const debouncedFetchAddress = useDebouncedCallback(
    (value: string, idx: number, city: string) => {
      if (!value) {
        setAddressOptions((prev) => {
          const next = [...prev];
          next[idx] = [];
          return next;
        });
        setLatitudes((prev) => {
          const next = [...prev];
          next[idx] = '';
          return next;
        });
        setLongitudes((prev) => {
          const next = [...prev];
          next[idx] = '';
          return next;
        });
        return;
      }
      const dadataQuery: {
        query: string;
        locations?: { city_type_full?: string; city?: string }[];
      } = {
        query: value,
        ...(city && { locations: [{ city }] })
      };
      apiDadata('addressSuggestion', dadataQuery).then((res) => {
        if (
          res.type === 'addressSuggestion' &&
          'data' in res &&
          Array.isArray(res.data.suggestions)
        ) {
          setAddressOptions((prev) => {
            const next = [...prev];
            next[idx] = res.data.suggestions.map((s: { value: string }) => ({
              value: s.value,
              label: s.value
            }));
            return next;
          });
          const suggestion = res.data.suggestions[0]?.data;
          setLongitudes((prev) => {
            const next = [...prev];
            next[idx] = suggestion?.geo_lon || '';
            return next;
          });
          setLatitudes((prev) => {
            const next = [...prev];
            next[idx] = suggestion?.geo_lat || '';
            return next;
          });
        }
      });
    },
    300
  );

  const handleCityInput = (value: string) => {
    setCityInput(value);
    debouncedFetchCities(value);
  };

  const handleAddressInput = (value: string, idx: number) => {
    setAddressInputs((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
    debouncedFetchAddress(value, idx, cityInput);
  };

  const handleAddClinic = () => {
    setClinicsCount((prev) => prev + 1);
    setAddressInputs((prev) => [...prev, '']);
    setLatitudes((prev) => [...prev, '']);
    setLongitudes((prev) => [...prev, '']);
    setAddressOptions((prev) => [...prev, []]);
  };

  const handlePhotoSelect = async (file: File | null) => {
    if (!file) {
      setPhoto(null);
      return;
    }

    setPhotoLoading(true);

    const response = await onUploadExpertPhoto(file);

    if (response.data?.filename) {
      setPhoto({
        filename: response.data.filename,
        previewUrl:
          getMediaBaseUrl() + '/brand_expert/tmp/' + response.data.filename
      });
    }

    setPhotoLoading(false);
  };

  if (state.status === 'success') {
    return (
      <div className={slots.success()}>
        <h3 className={slots.title()}>{t('doctor.form.successTitle')}</h3>
        <p className={slots.text()}>{t('doctor.form.successText')}</p>
        <UiKitButtonBase
          className={slots.buttonSuccess()}
          onClick={onReset}
        >
          {t('doctor.form.successButton')}
        </UiKitButtonBase>
      </div>
    );
  }

  return (
    <div className={slots.base()}>
      <h3 className={slots.title()}>{t('doctor.form.title')}</h3>
      <p className={slots.text()}>{t('doctor.form.subtitle')}</p>

      <form
        className={slots.form()}
        action={action}
        noValidate
      >
        <div className={slots.block()}>
          <p className={slots.subtitle()}>{t('doctor.form.sections.about')}</p>

          <div className={slots.inputs()}>
            <UiKitInputBase
              {...getFieldProps('full_name')}
              label={t('doctor.form.fields.fullName')}
              placeholder={t('doctor.form.fields.fullName')}
              autoComplete="username"
              className={slots.input()}
              required
            />
            <UiKitInputBase
              {...getFieldProps('seniority')}
              type="number"
              label={t('doctor.form.fields.seniorityYear')}
              placeholder={t('doctor.form.fields.seniorityYear')}
              className={slots.input()}
              maxLength={4}
              min={0}
              onInput={(e) => {
                e.currentTarget.value = e.currentTarget.value.slice(0, 4);
              }}
              required
            />
            <UiKitTextareaBase
              {...getFieldProps('regalia')}
              label={t('doctor.form.fields.regalia')}
              placeholder={t('doctor.form.fields.regalia')}
              rows={4}
              maxLength={400}
              showCount
              className={slots.input()}
              required
            />
            <UiKitSelectMultiple
              label={t('doctor.form.fields.specialty')}
              placeholder={t('doctor.form.fields.specialty')}
              options={occupations.map((item) => ({
                value: item.id,
                label: item.label
              }))}
              className={slots.input()}
              selectedOptions={occupations
                .filter((item) => occupationList.includes(item.id))
                .map((item) => ({ value: item.id, label: item.label }))}
              onChange={(opts) => setOccupationList(opts.map((o) => o.value))}
            />
            {state.errors?.occupation && (
              <span className={slots.error()}>{state.errors?.occupation}</span>
            )}
            <input
              type="hidden"
              name="occupation"
              value={JSON.stringify(occupationList)}
            />
            <div>
              <UiKitSelectMultiple
                label={t('doctor.form.fields.qualification')}
                placeholder={t('doctor.form.fields.qualification')}
                options={qualifications.map((item) => ({
                  value: item.id,
                  label: item.label
                }))}
                className={slots.input()}
                selectedOptions={qualifications
                  .filter((item) => qualificationList.includes(item.id))
                  .map((item) => ({ value: item.id, label: item.label }))}
                onChange={(opts) =>
                  setQualificationList(opts.map((o) => o.value))
                }
              />
              <input
                type="hidden"
                name="qualification"
                value={JSON.stringify(qualificationList)}
              />
            </div>
            <div>
              <UiKitAutocompleteBase
                label={t('doctor.form.fields.city')}
                placeholder={t('doctor.form.fields.city')}
                startAdornment={<IconSearch />}
                options={cityOptions}
                onChange={handleCityInput}
                className={slots.input()}
                value={cityInput}
                required
                notResetValue
              />
              {state.errors?.city && (
                <span className={slots.error()}>{state.errors?.city}</span>
              )}
              <input
                {...getFieldProps('city')}
                type="hidden"
                value={cityInput}
              />
            </div>
            <UiKitInputBase
              {...getFieldProps('vk')}
              className={slots.input()}
              type="text"
              label={t('doctor.form.fields.vk')}
              placeholder={t('doctor.form.fields.vk')}
              helperText="https://vk.com/username"
            />
            <UiKitInputBase
              {...getFieldProps('telegram')}
              type="text"
              label={t('doctor.form.fields.telegram')}
              placeholder={t('doctor.form.fields.telegram')}
              helperText="https://t.me/username"
              className={slots.input()}
            />
            <UiKitInputBase
              {...getFieldProps('taplink')}
              type="text"
              label={t('doctor.form.fields.taplink')}
              placeholder={t('doctor.form.fields.taplink')}
              className={slots.input()}
              helperText="https://taplink.ru/username"
            />
          </div>
        </div>

        {[...Array(clinicsCount)].map((_, index) => (
          <div
            key={index}
            className={slots.block()}
          >
            <p className={slots.subtitle()}>
              {t('doctor.form.sections.clinic')} {index + 1}
            </p>
            <div className={slots.inputs()}>
              <div className={slots.inputWrapper()}>
                <UiKitInputBase
                  {...getFieldProps(`clinics[${index}].name`)}
                  type="text"
                  label={t('doctor.form.fields.clinicName')}
                  placeholder={t('doctor.form.fields.clinicName')}
                  className={slots.input()}
                  required
                />
              </div>

              <div className={slots.inputWrapper()}>
                <UiKitAutocompleteBase
                  {...getFieldProps(`clinics[${index}].address`)}
                  label={t('doctor.form.fields.clinicAddress')}
                  placeholder={t('doctor.form.fields.clinicAddress')}
                  options={addressOptions[index] || []}
                  value={addressInputs[index] || ''}
                  onChange={(val) => handleAddressInput(val, index)}
                  className={slots.input()}
                  classNameInput={slots.inputSearch()}
                  required
                  notResetValue
                />
                <input
                  {...getFieldProps(`clinics[${index}].address`)}
                  type="hidden"
                  value={addressInputs[index] || ''}
                />
                <input
                  {...getFieldProps(`clinics[${index}].latitude`)}
                  type="hidden"
                  value={latitudes[index] || ''}
                />
                <input
                  {...getFieldProps(`clinics[${index}].longitude`)}
                  type="hidden"
                  value={longitudes[index] || ''}
                />
              </div>
              <div className={slots.inputWrapper()}>
                <UiKitInputBase
                  {...getFieldProps(`clinics[${index}].telephone`)}
                  type="tel"
                  label={t('doctor.form.fields.clinicPhone')}
                  placeholder={t('doctor.form.fields.clinicPhone')}
                  mask="+7 (999) 999-99-99"
                  autoComplete="tel"
                  className={slots.input()}
                  required
                />
              </div>
              {index === clinicsCount - 1 && (
                <UiKitButtonBase
                  type="button"
                  color="ghost"
                  className={slots.addButton()}
                  onClick={handleAddClinic}
                >
                  <div className={slots.addButtonContent()}>
                    {t('doctor.form.buttons.addClinic')}
                    <IconPlus />
                  </div>
                </UiKitButtonBase>
              )}
            </div>
          </div>
        ))}

        <div className={slots.block()}>
          <UiKitInputPhoto
            value={photo}
            loading={photoLoading}
            onSelect={handlePhotoSelect}
            title={t('Add a profile photo')}
            maxSize={1024 * 1024}
            helperText={
              <>
                {t('Picture')} 1:1 ({t('square')}) {SUPPORTED_IMAGE_FORMATS}{' '}
                {t('weighing up to')} {1} MB.
              </>
            }
            note={
              <>
                {t('It is advisable to upload photos in a white medical coat')}.
                {''}
                <br className={slots.br()} />
                <a
                  href={`${getMediaBaseUrl()}/wysiwyg/expert-photo-recommendation.pdf`}
                  className={slots.link()}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('Recommendations for taking photos on your phone')}.
                </a>
              </>
            }
          />
          <input
            {...getFieldProps('photo')}
            type="hidden"
            value={photo?.filename}
          />
        </div>

        <div className={slots.block()}>
          <UiKitButtonBase
            disabled={pending || !isDirty || photoLoading}
            className={slots.button()}
            type="submit"
          >
            {t('Send')}
          </UiKitButtonBase>

          <p className={slots.disclaimer()}>
            {t.rich('form.label.agreementDoctor', {
              link: (chunks) => (
                <a
                  className={slots.link()}
                  href="/legal/politika-konfidentsialnosti/"
                  target="_blank"
                  rel="noreferrer"
                >
                  {chunks}
                </a>
              )
            })}
          </p>
        </div>
      </form>
    </div>
  );
}
